import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../locales/translations';
import { LOGO_URL } from '../assets/index';

const ThankYou = () => {
  const { language } = useLanguage();
  const text = t[language].thankYou;
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  const stepIcons = [FiIcons.FiDownload, FiIcons.FiKey, FiIcons.FiZap];

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setVisible(true);
    } else {
      navigate('/');
    }
  }, [navigate]);

  if (!visible) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-4 relative overflow-hidden">
      <motion.div
        className="absolute w-[500px] h-[500px] bg-green-600/15 rounded-full blur-[120px] top-1/4 left-1/2 -translate-x-1/2 pointer-events-none"
        animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 6, repeat: Infinity }}
      />
      <motion.div
        className="absolute w-[300px] h-[300px] bg-purple-600/10 rounded-full blur-[100px] bottom-1/4 right-0 pointer-events-none"
        animate={{ scale: [1, 1.15, 1] }}
        transition={{ duration: 8, repeat: Infinity }}
      />

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="relative z-10 max-w-lg w-full text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.2 }}
          className="w-24 h-24 mx-auto mb-8 rounded-full bg-gradient-to-br from-green-400/20 to-emerald-600/20 border-2 border-green-500/40 flex items-center justify-center"
        >
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.5, type: 'spring', stiffness: 300 }}>
            <SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-400 text-5xl" />
          </motion.div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-bold mb-5">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            {text.confirmed}
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-4 leading-tight">
            {text.title} <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">{text.titleHighlight}</span> 🎉
          </h1>
          <p className="text-gray-400 text-base sm:text-lg leading-relaxed mb-10">
            {text.subtitle}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white/3 border border-white/8 rounded-2xl p-6 mb-8 text-left space-y-4"
        >
          <p className="text-white font-bold text-sm uppercase tracking-wider mb-4">{text.nextStepsLabel}</p>
          {text.steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 + i * 0.15 }}
              className="flex items-center gap-4"
            >
              <div className="w-10 h-10 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center shrink-0">
                <SafeIcon icon={stepIcons[i]} className="text-green-400 text-base" />
              </div>
              <span className="text-gray-300 text-sm sm:text-base">{step}</span>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="flex flex-col sm:flex-row gap-3 justify-center"
        >
          <motion.button
            onClick={() => navigate('/')}
            className="px-8 py-3.5 bg-white text-gray-900 rounded-full font-black text-sm flex items-center justify-center gap-2 group relative overflow-hidden"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
          >
            <motion.div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <span className="relative z-10 group-hover:text-white transition-colors">{text.backBtn}</span>
            <SafeIcon icon={FiIcons.FiHome} className="relative z-10 group-hover:text-white transition-colors" />
          </motion.button>
          <motion.a
            href="mailto:suporte@lovago.net"
            className="px-8 py-3.5 glass border border-white/10 text-white rounded-full font-bold text-sm flex items-center justify-center gap-2 hover:bg-white/5 transition-colors"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
          >
            <SafeIcon icon={FiIcons.FiMail} className="text-gray-400" />
            {text.supportBtn}
          </motion.a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="mt-10 flex items-center justify-center gap-2 opacity-40"
        >
          <img src={LOGO_URL} alt="LovaGO" className="w-6 h-6 object-contain" />
          <span className="text-sm font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-500">LovaGO</span>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ThankYou;